import { useCallback, useEffect, useRef, useState } from 'react';
import { callAction } from '../../app/actions';
import { apiRequest } from '../../api/client';
import { registerHandle } from '../../app/handles';

/**
 * One log entry, in full: what was asked of the agent, what it managed, and what got in its
 * way.
 *
 * Opened from two places that know nothing about each other — the Document logs table and the
 * per-document timeline beside the doc view — which is why it takes an id and nothing else.
 * That seam is what let the logs panel port on its own, months of work earlier in this branch.
 *
 * RESTORE IS NON-DESTRUCTIVE, and the confirmation says so: the current text becomes its own
 * version rather than being overwritten. Anybody who reads that sentence and clicks has been
 * told the truth, which is the only reason a one-click restore is reasonable here at all.
 */

export interface LogDetailHandle {
  open: (id: string) => void;
  close: () => void;
}

interface Coverage {
  status: string;
  instruction: string;
  note?: string;
}

interface Revision {
  id: string;
  version: number;
  locale: string;
  label?: string;
  createdAt: string;
  actorLabel?: string;
  source?: string;
  status?: string;
  model?: string;
  targetName?: string;
  sourceLocale?: string;
  sourceVersion?: number | null;
  imagesEmbedded?: number;
  imagesDropped?: number;
  inputTokens?: number;
  outputTokens?: number;
  durationMs?: number;
  errorText?: string;
  promptSnapshot?: string;
  agentNotes?: string;
  // Mostly "was this source available", but a key may also COUNT something the run did
  // (groundingSkipped: how many stub pages the grounding pool refused). A count renders
  // with its number; a boolean renders on/off as it always has.
  contextFlags?: Record<string, boolean | number>;
  coverage?: Coverage[];
  issues?: { kind: string; detail: string }[];
}

/** A definition-list row, omitted entirely when there is nothing to say. */
function KV({ k, v }: { k: string; v?: string | number | null }) {
  if (!v) return null;
  return (
    <>
      <dt>{k}</dt>
      <dd>{String(v)}</dd>
    </>
  );
}

/** `useSemanticGrounding` -> "use semantic grounding". */
const humanise = (k: string) => k.replace(/([A-Z])/g, ' $1').toLowerCase();

export function LogDetail({ host }: { host: HTMLElement }) {
  const [id, setId] = useState<string | null>(null);
  const [rev, setRev] = useState<Revision | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The overlay is display:flex rather than a `.show` class — it predates that convention and
  // the stylesheet still says so.
  useEffect(() => {
    host.style.display = id ? 'flex' : 'none';
  }, [id, host]);

  const api = useRef<LogDetailHandle>({ open: () => {}, close: () => {} });
  api.current.open = useCallback((next: string) => {
    setId(next);
    setRev(null);
    setError(null);
  }, []);
  api.current.close = useCallback(() => setId(null), []);
  useEffect(() => {
    registerHandle('logDetail', api.current);
  }, []);

  useEffect(() => {
    if (!id) return;
    let live = true;
    void apiRequest<Revision>(`/api/docs/revisions/${encodeURIComponent(id)}`).then(
      (r) => live && setRev(r),
      (e: Error) => live && setError(e.message),
    );
    return () => {
      live = false;
    };
  }, [id]);

  if (!id) return null;

  const restore = async () => {
    if (
      !confirm(
        `Restore V${rev ? rev.version : '?'}?\n\n` +
          'The current text is kept as its own version, so nothing is lost.',
      )
    ) {
      return;
    }
    try {
      await apiRequest(`/api/docs/revisions/${encodeURIComponent(id)}/restore`, { method: 'POST' });
      setId(null);
      // Whatever is on screen was rendered from the text that has just been replaced, so the
      // doc view reloads it. Which reload that is depends on the mode, and the vanilla side
      // still owns both.
      callAction('afterRevisionRestored');
    } catch (e) {
      alert(`Could not restore this version.\n${e instanceof Error ? e.message : ''}`);
    }
  };

  const flags = rev?.contextFlags || {};
  const flagKeys = Object.keys(flags);
  const coverage = rev?.coverage || [];
  const issues = rev?.issues || [];

  return (
    <>
      <div className="logdetail-head">
        <span id="logdetail-title">
          {rev ? `V${rev.version} · ${(rev.locale || '').toUpperCase()} · ${rev.label || ''}` : ''}
        </span>
        <button className="pbtn" id="logdetail-restore" onClick={restore}>
          Restore this version
        </button>
        <button className="pbtn" id="logdetail-close" onClick={() => setId(null)}>
          Close
        </button>
      </div>

      <div className="logdetail-body" id="logdetail-body">
        {error ? (
          <div style={{ color: 'var(--ink-faint)' }}>Could not load this entry ({error}).</div>
        ) : !rev ? (
          <div style={{ color: 'var(--ink-faint)' }}>Loading…</div>
        ) : (
          <>
            <div className="ld-sec">
              <h4>Run</h4>
              <dl className="ld-kv">
                <KV k="When" v={new Date(rev.createdAt).toLocaleString()} />
                <KV k="By" v={rev.actorLabel} />
                <KV k="Source" v={rev.source} />
                <KV k="Status" v={rev.status} />
                <KV k="Model" v={rev.model} />
                <KV k="System" v={rev.targetName} />
                <KV
                  k="Translated from"
                  v={
                    rev.sourceLocale
                      ? `${rev.sourceLocale.toUpperCase()} V${rev.sourceVersion ?? '?'}`
                      : ''
                  }
                />
                <KV
                  k="Images"
                  v={`${rev.imagesEmbedded ?? 0} embedded${
                    rev.imagesDropped ? `, ${rev.imagesDropped} invented and removed` : ''
                  }`}
                />
                <KV
                  k="Tokens"
                  v={rev.inputTokens ? `${rev.inputTokens} in / ${rev.outputTokens} out` : ''}
                />
                <KV k="Took" v={rev.durationMs ? `${(rev.durationMs / 1000).toFixed(1)}s` : ''} />
              </dl>
            </div>

            {rev.errorText ? (
              <div className="ld-sec">
                <h4>Error</h4>
                <div className="ld-pre">{rev.errorText}</div>
              </div>
            ) : null}

            {/* The prompt as it was AT THE TIME, not as it is now — the whole point of a log
                is that it can be read after the prompt has been edited. */}
            {rev.promptSnapshot ? (
              <div className="ld-sec">
                <h4>Prompt used</h4>
                <div className="ld-pre">{rev.promptSnapshot}</div>
              </div>
            ) : null}

            {flagKeys.length ? (
              <div className="ld-sec">
                <h4>Context available</h4>
                <div className="ld-flags">
                  {flagKeys.map((k) => (
                    <span key={k} className={flags[k] ? 'ld-flag on' : 'ld-flag'}>
                      {typeof flags[k] === 'number' ? `${humanise(k)}: ${flags[k]}` : humanise(k)}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}

            {coverage.length ? (
              <div className="ld-sec">
                <h4>What it did with the instructions</h4>
                <table className="ld-cov">
                  <tbody>
                    {coverage.map((c, i) => (
                      <tr key={i}>
                        <td>
                          <span className={`ld-badge ${c.status}`}>{c.status}</span>
                        </td>
                        <td>
                          {c.instruction}
                          {c.note ? (
                            <div style={{ color: 'var(--ink-faint)', marginTop: 3 }}>{c.note}</div>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}

            {issues.length ? (
              <div className="ld-sec">
                <h4>Issues</h4>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, lineHeight: 1.6 }}>
                  {issues.map((i, n) => (
                    <li key={n}>
                      <b>{i.kind}</b> — {i.detail}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {rev.agentNotes ? (
              <div className="ld-sec">
                <h4>Notes</h4>
                <div className="ld-pre">{rev.agentNotes}</div>
              </div>
            ) : null}
          </>
        )}
      </div>
    </>
  );
}
